import { create } from 'zustand'
import { useAuthStore } from '@/shared/stores/auth'
import type { StructuredWebSearchResult } from '@/types/web-search'
import type { ImportProgress, ImportSummary } from '@/types/rich-content'
import type { MaterialItem, WoPayload } from '@/types/artifacts'
import type { ResolvedSource } from '@/features/citations/source-resolver'
import type { DashboardSpec } from '@/features/artifacts/types'
import type { LtpScheduleEnvelope } from '@/types/ltp-schedule'

interface ArtifactSource {
  messageId?: string
  conversationId?: string
}

interface ArtifactBase {
  id: string
  title: string
  source?: ArtifactSource
  /** Axis Insights — versao do artefato, como veio no `artifact_start`. So o painel de indicadores a mostra. */
  version?: number
  /**
   * Axis Insights — `true` entre o `artifact_start` e o `artifact_end`: o painel
   * ja esta aberto com o titulo e os esqueletos no lugar, e os blocos chegam
   * na mesma caixa quando o spec fecha. E o que faz o streaming nao saltar.
   */
  streaming?: boolean
}

/**
 * Discriminated union — P0-1 (2026-08-14). Each `open()` caller must hand the
 * adapter the exact domain shape it renders; a payload no adapter recognizes
 * simply cannot be constructed, so the JSON-dump fallback that used to live
 * in every adapter has nowhere left to trigger from.
 *
 * `citation` — P0-3 (2026-08-14): the real-source panel for `[doc:]`/`[equip:]`
 * chips once `CitationChip` resolves them (`ResolvedSource`, never the old
 * fabricated placeholder — see `src/features/citations/source-resolver.ts`).
 */
export type Artifact =
  | (ArtifactBase & { type: 'plan'; payload: StructuredWebSearchResult })
  | (ArtifactBase & { type: 'materials'; payload: { items: MaterialItem[] } })
  | (ArtifactBase & { type: 'wo'; payload: WoPayload })
  | (ArtifactBase & { type: 'import'; payload: ImportProgress | ImportSummary })
  | (ArtifactBase & { type: 'citation'; payload: ResolvedSource })
  // Axis Insights — painel de indicadores. O payload e o DashboardSpec ja
  // validado pelo motor (`parseDashboardSpec`); a tela nunca monta um spec.
  | (ArtifactBase & { type: 'insights'; payload: DashboardSpec })
  // Cronograma 52 semanas (STO-528/F5) — envelope `ltp_schedule` vindo do
  // `ScheduleCard` compacto na bolha; o `ScheduleBoard` re-busca a grade
  // completa via `getSchedule`, este payload so carrega o resumo inicial.
  | (ArtifactBase & { type: 'schedule'; payload: LtpScheduleEnvelope; preloaded?: boolean })

export type ArtifactType = Artifact['type']

interface ArtifactsState {
  active: Artifact | null
  open: (a: Artifact) => void
  close: () => void
}

export const useArtifactsStore = create<ArtifactsState>((set) => ({
  active: null,
  open: (a) => set({ active: a }),
  close: () => set({ active: null }),
}))

// Clear retained payloads synchronously, even when the panel is not mounted.
useAuthStore.subscribe((next, previous) => {
  if (next.tenantId !== previous.tenantId || next.userId !== previous.userId || next._getToken !== previous._getToken)
    useArtifactsStore.getState().close()
})
